// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from 'react'
import api from '@crm-src/lib/api'

export interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  full_name: string
  active: boolean
  created_at: string
  updated_at: string
}

interface UseUsersResult {
  users: User[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

export const useUsers = (): UseUsersResult => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch all active users - you may need to adjust this endpoint
      const response = await api.get('/users')
      
      // Handle both direct array and wrapped response formats
      const usersData = response.data.data || response.data.users || response.data
      
      if (Array.isArray(usersData)) {
        setUsers(usersData.filter(user => user.active !== false))
      } else {
        console.warn('Unexpected users data format:', response.data)
        setUsers([])
      }
    } catch (err: any) {
      // Handle permission errors gracefully (403 Forbidden for non-admin users)
      if (err?.response?.status === 403) {
        // Non-admin users don't have access to users endpoint
        // This is expected behavior - set empty users array and no error
        setUsers([])
        setError(null) // Don't show error for permission issues
        return
      }
      
      // Ignore cancelled requests
      const isCancelled = err.code === 'ERR_NETWORK_CHANGED' || 
                         err.code === 'ERR_CANCELED' ||
                         (err.message === 'Network Error' && !err.response && !err.request?.status);
      
      if (isCancelled) {
        setUsers([])
        setLoading(false)
        return
      }
      
      // Only log and show errors for actual failures
      console.error('Error fetching users:', err)
      setError(err?.response?.data?.error || err?.message || 'Failed to fetch users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  return {
    users,
    loading,
    error,
    refresh: fetchUsers
  }
}